import { isAxiosError } from 'axios';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';

import { instance } from '@/interfaces';
import { tokenAtom } from '@/interfaces/auth';
import { selectedGroupIdAtom } from '@/interfaces/group';

import type { Getter } from 'jotai';
import type { AtomWithQueryResult } from 'jotai-tanstack-query';
import type { APIResponse } from '@/@types/types';
import type { UserInfo, UserInfoData, TokenInfo, GroupMemberInfo, SiteMemberInfo } from '@/@types/user';

const MEMBER_QUERY_NAME = 'MEMBER_';

export const userInfoAtom = atomWithQuery<UserInfo, Error, UserInfo, [string, TokenInfo]>((get) => ({
  queryKey: [MEMBER_QUERY_NAME + 'LOGIN_USER', get(tokenAtom)],
  queryFn: async ({ queryKey: [, token] }) => {
    //Log out 시 UserInfo를 리셋하기 위해 token을 받아서 처리하도록 구현
    //queryFn 에서 토큰이 사라지면 token값이 갱신되어도 refetch 되지 않음
    //이를 처리하기 위한 코드가 많아지므로 token값을 체크하여 없는 경우에는 API를 호출하지 않도록 처리
    if (token && token.accessToken) {
      const res = await getMemberInfo();
      const retVal: UserInfo = {
        id: res.id,
        email: res.email,
        name: res.info.name,
        site: res.info.site,
        isAdmin: res.info.site[0].role === 'ADMIN',
      };

      return retVal;
    } else {
      return Promise.reject();
    }
  },
  retry: 0,
}));
const getMemberInfo = async () => {
  const { data } = await instance.get<APIResponse<UserInfoData>>('/member/info');
  return data.data;
};

export const patchUserAtom = atomWithMutation(() => ({
  mutationKey: [MEMBER_QUERY_NAME + 'LOGIN_USER'],
  mutationFn: async (body: Pick<UserInfo, 'name'>) => {
    return patchUser(body);
  },
}));
const patchUser = async (body: Pick<UserInfo, 'name'>) => {
  return await instance.patch('/member/info', body);
};

export const groupMembersAtom = atomWithQuery((get: Getter) => ({
  queryKey: [MEMBER_QUERY_NAME + 'GROUP', get(selectedGroupIdAtom)],
  queryFn: async ({ queryKey: [, groupId] }) => {
    if (groupId === '') return [];

    const data = await getGroupMembers(groupId);
    return data;
  },
}));
async function getGroupMembers(groupId: string) {
  try {
    const { data } = await instance.get<APIResponse<GroupMemberInfo[]>>(`/member/group/${groupId}`);
    return data.data;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[getGroupMembers] API error code:', err.response.data.errorCode);
      return [];
    }
    console.error('[getGroupMembers] Unknown error', err);
    return [];
  }
}

export const siteMembersAtom = atomWithQuery((get: Getter) => ({
  queryKey: [MEMBER_QUERY_NAME + 'SITE', get(userInfoAtom)],
  queryFn: async ({ queryKey: [, userInfoAtomValue] }) => {
    const { data: userInfo } = userInfoAtomValue as AtomWithQueryResult<UserInfo>;

    // TODO: Get current site id from API
    const data = await getSiteMembers(userInfo.site[0].id);
    return data;
  },
}));
async function getSiteMembers(siteId: string) {
  try {
    const { data } = await instance.get<APIResponse<SiteMemberInfo[]>>(`/member/site/${siteId}`);
    return data.data;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[getSiteMembers] API error code:', err.response.data.errorCode);
      return [];
    }
    console.error('[getSiteMembers] Unknown error', err);
    return [];
  }
}

export const deleteMemberAtom = atomWithMutation((get) => ({
  mutationFn: async (userId: string) => {
    const { data: userInfo } = get(userInfoAtom);
    const siteId = userInfo.site[0].id;

    const isSuccess = await deleteMember(siteId, userId);
    return isSuccess;
  },
}));
async function deleteMember(siteId: string, userId: string) {
  try {
    const { data } = await instance.delete<APIResponse>(`/member/${userId}/user`, { params: { siteId } });
    if (data.errorCode !== 'OK') {
      console.error('[deleteMember] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error('[deleteMember] API error code:', err.response.data.errorCode);
    } else {
      console.error('[deleteMember] Unknown error', err);
    }
    return false;
  }
}

export const inviteMemberAtom = atomWithMutation((get) => ({
  mutationFn: async (email: string) => {
    const { data: userInfo } = get(userInfoAtom);
    const siteId = userInfo.site[0].id;

    const isSuccess = await inviteMember(siteId, email);
    return isSuccess;
  },
}));
async function inviteMember(siteId: string, email: string) {
  try {
    const { data } = await instance.post<APIResponse>(`/member`, { siteId, email });
    if (data.errorCode !== 'OK') {
      console.error('[inviteMember] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error('[inviteMember] API error code:', err.response.data.errorCode);
    } else {
      console.error('[inviteMember] Unknown error', err);
    }
    return false;
  }
}
